import { Button } from '@aecom-ai-engineering/react-components';
import { useServiceName, useProjectId } from '@consigli/hooks';
import { ServiceCategory } from '@consigli/types';
import clsx from 'clsx';
import { X } from 'lucide-react';
import { type FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { AgentChatLogoMark } from '@/components/agent-chat-logo';
import { useAgentChatViewport } from '@/layouts/use-agent-chat-viewport';
import { TabConfig } from '@/util/types';

import { WorkspaceTabsItem } from './workspace-tabs-item';

interface WorkspaceTabsProps {
  hideTabs?: boolean;
  tabs: TabConfig[];
  serviceCategory: ServiceCategory;
  serviceId: string;
  scrollListComponent?: ReactNode;
}

export const WorkspaceTabs: FC<WorkspaceTabsProps> = ({
  hideTabs,
  tabs,
  serviceCategory,
  serviceId,
  scrollListComponent,
}) => {
  const serviceName = useServiceName();
  const projectId = useProjectId();
  const { t } = useTranslation();
  const {
    agentChatToggleRef,
    closeAgentChat,
    hasAgentChatEntered,
    isAgentChatMaximized,
    isAgentChatOpen,
    isAgentChatResizing,
    openAgentChat,
  } = useAgentChatViewport();
  const showAgentChatClose = isAgentChatOpen && hasAgentChatEntered;
  const showAgentChatToggle = !hideTabs && serviceCategory === ServiceCategory.INSIGHT;

  return (
    <div className="w-full">
      <div className="flex items-center gap-4 mx-auto 2xl:pb-4 pb-2 px-8 max-w-[2000px]">
        <div className="flex-1 min-w-0">{scrollListComponent}</div>
        <nav className="shrink-0">
          {!hideTabs && (
            <div className="flex">
              {tabs.map(({ href, name }, index) => (
                <div key={name} className={`${index !== tabs.length - 1 ? 'mr-3' : ''}`}>
                  <WorkspaceTabsItem name={name} href={href(serviceName, projectId, serviceId)} />
                </div>
              ))}
            </div>
          )}
        </nav>
        <div className="flex-1 min-w-0" aria-hidden />
      </div>
      {showAgentChatToggle && !isAgentChatMaximized && (
        <div
          className={clsx(
            'fixed bottom-[var(--ds-space-8)] z-[40] transition-[right] motion-reduce:transition-none',
            isAgentChatResizing ? 'duration-0' : 'duration-300',
            isAgentChatOpen
              ? 'right-[var(--ds-space-8)] md:right-[calc(var(--agent-chat-width)+var(--ds-space-8))]'
              : 'right-[var(--ds-space-8)]',
          )}
        >
          <Button
            ref={agentChatToggleRef}
            aria-label={isAgentChatOpen ? t('prop-chat.close') : t('servicetabs.propchat')}
            aria-controls="agent-chat-drawer"
            aria-expanded={isAgentChatOpen}
            variant="solid"
            size="lg"
            // A brand-green pill is not one of the DS button variants, so the
            // colour, radius and width animation are overridden here. `solid`
            // still supplies the invert text colour and `lg` the icon sizing.
            // The shadow and 180ms duration stay literal: design-tokens 0.5.1
            // ships colour, radius, space and typography only — no elevation or
            // motion scales to reference.
            className={clsx(
              'ds-body-font relative h-[var(--ds-space-10)] min-w-0 overflow-hidden rounded-[var(--ds-radius-infinite)] border border-solid border-transparent bg-[var(--ds-color-bg-brand)] p-0 text-base font-normal tracking-normal shadow-[0_20px_12.5px_rgba(0,0,0,0.1),0_8px_5px_rgba(0,0,0,0.1)] transition-[width,padding,border-color,background-color] duration-[180ms] ease-in-out motion-reduce:transition-none hover:border-[var(--ds-color-bg-brand-light)] hover:bg-[var(--ds-color-bg-brand)]',
              showAgentChatClose ? 'w-[var(--ds-space-10)]' : 'w-[142px]',
            )}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              if (isAgentChatOpen) closeAgentChat();
              else openAgentChat();
            }}
          >
            <span
              aria-hidden={showAgentChatClose}
              className={clsx(
                'absolute inset-0 flex items-center justify-center gap-[var(--ds-space-1)] whitespace-nowrap transition-opacity duration-[90ms] motion-reduce:transition-none',
                showAgentChatClose ? 'opacity-0' : 'opacity-100',
              )}
            >
              <AgentChatLogoMark className="size-[var(--ds-space-6)] shrink-0" />
              {t('servicetabs.propchat')}
            </span>
            <X
              aria-hidden="true"
              className={clsx(
                'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-[opacity,transform] duration-[180ms] ease-in-out motion-reduce:transition-none',
                showAgentChatClose
                  ? 'rotate-0 scale-100 opacity-100'
                  : '-rotate-90 scale-75 opacity-0',
              )}
            />
          </Button>
        </div>
      )}
    </div>
  );
};
